import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Sparkles, Upload, FileText, Plus, ShieldCheck, ScrollText, BookMarked } from "lucide-react";
import { useI18n, QC_LABELS } from "../../i18n";
import { useAuth } from "../../context/AuthContext";
import { api, apiError, API } from "../../lib/api";
import { StatusBadge, RiskPill, ConfidenceBadge, WorkflowPipeline } from "../../components/app/widgets";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../components/ui/tabs";
import { Checkbox } from "../../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";

const CATEGORIES = ["Registration", "Output VAT", "Input VAT", "Zero Rating", "Exemption", "Imports", "Exports", "Reverse Charge", "Tax Invoice", "E-Invoicing", "Credit Notes", "Debit Notes", "VAT Group", "Related Parties", "Capital Assets", "Refunds", "Reconciliation", "ZATCA Audit", "Documentation", "Timing", "Place of Supply"];
const SOURCES = [
  { t: { en: "Saudi VAT Law", ar: "نظام ضريبة القيمة المضافة" }, s: { en: "Primary legislation", ar: "التشريع الأساسي" } },
  { t: { en: "VAT Implementing Regulations", ar: "اللائحة التنفيذية للضريبة" }, s: { en: "Regulations", ar: "لوائح" } },
  { t: { en: "ZATCA Guidelines & Manuals", ar: "إرشادات وأدلة الهيئة" }, s: { en: "Official guidance", ar: "إرشاد رسمي" } },
  { t: { en: "Public Rulings & Circulars", ar: "القرارات والتعاميم" }, s: { en: "Official rulings", ar: "قرارات رسمية" } },
  { t: { en: "GSTC Decisions", ar: "قرارات الأمانة العامة للجان الضريبية" }, s: { en: "Precedents", ar: "سوابق" } },
];

export default function CaseDetail() {
  const { id } = useParams();
  const { t, lang, dir } = useI18n();
  const { user } = useAuth();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [comment, setComment] = useState("");
  const [newFinding, setNewFinding] = useState({ category: "Reconciliation", description: "", risk_level: "medium", recommendation: "" });
  const [newReq, setNewReq] = useState({ name: "", description: "" });
  const [timeForm, setTimeForm] = useState({ minutes: "", note: "" });
  const [timer, setTimer] = useState({ running: false, startedAt: null, elapsed: 0 });
  const [relDecisions, setRelDecisions] = useState([]);
  const timerRef = useRef(null);
  const fileRef = useRef(null);

  const isClient = ["client_admin", "client_user"].includes(user.role);

  useEffect(() => {
    if (!id) return;
    api.get(`/cases/${id}/related-decisions`).then((r) => setRelDecisions(r.data.decisions || [])).catch(() => {});
  }, [id, c && c.status, c && c.ai_confirmed]);
  const isSpecialist = ["vat_specialist", "platform_admin"].includes(user.role);
  const isSupervisor = ["supervisor", "platform_admin"].includes(user.role);
  const isQuality = ["quality_reviewer", "platform_admin"].includes(user.role);

  const load = useCallback(() => { api.get(`/cases/${id}`).then((r) => setC(r.data)).catch(() => {}); }, [id]);
  useEffect(load, [load]);

  const delDraft = async (did) => { try { await api.delete(`/cases/${id}/drafts/${did}`); load(); } catch (e) { toast.error(apiError(e)); } };

  if (!c) return <div className="text-muted-foreground">{t("loading")}</div>;

  const gateAi = c.service_type === "advisory";
  const aiReleased = !gateAi || c.ai_confirmed || ["completed", "client_approval", "closed"].includes(c.status);

  const doAction = async (action) => {
    setBusy(true);
    try { await api.post(`/cases/${id}/action`, { action, comment: "" }); toast.success(t("saved")); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const runAI = async () => {
    setBusy(true);
    try { await api.post(`/cases/${id}/run-ai`); toast.success("AI"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  const addFinding = async () => {
    if (!newFinding.description) return;
    try { await api.post(`/cases/${id}/findings`, newFinding); setNewFinding({ ...newFinding, description: "", recommendation: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const updateFinding = async (fid, body) => { try { await api.put(`/cases/${id}/findings/${fid}`, body); load(); } catch (e) { toast.error(apiError(e)); } };
  const addReq = async () => {
    if (!newReq.name) return;
    try { await api.post(`/cases/${id}/document-requests`, { ...newReq, required: true }); setNewReq({ name: "", description: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const toggleQC = async (key, checked) => { try { await api.put(`/cases/${id}/qc`, { key, checked }); load(); } catch (e) { toast.error(apiError(e)); } };
  const sendComment = async () => { if (!comment.trim()) return; try { await api.post(`/cases/${id}/comments`, { text: comment }); setComment(""); load(); } catch (e) { toast.error(apiError(e)); } };
  const logTime = async () => {
    const m = parseInt(timeForm.minutes || "0");
    if (!m) return;
    try { await api.post(`/cases/${id}/time-entries`, { minutes: m, note: timeForm.note }); setTimeForm({ minutes: "", note: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const startTimer = () => { const s = Date.now(); setTimer({ running: true, startedAt: s, elapsed: 0 }); timerRef.current = setInterval(() => setTimer((tm) => ({ ...tm, elapsed: Math.floor((Date.now() - s) / 1000) })), 1000); };
  const stopTimer = async () => {
    clearInterval(timerRef.current);
    const started = timer.startedAt;
    setTimer({ running: false, startedAt: null, elapsed: 0 });
    const mins = Math.max(1, Math.round((Date.now() - started) / 60000));
    try { await api.post(`/cases/${id}/time-entries`, { minutes: mins, note: timeForm.note || t("timer_session") }); setTimeForm({ minutes: "", note: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("folder", c.service_type === "zatca_audit" ? "ZATCA" : "Reports"); fd.append("case_id", id);
    try { await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(t("upload")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const actions = [];
  if (isClient) {
    if (["new", "awaiting_documents", "additional_info_required"].includes(c.status)) actions.push(["submit_documents", t("upload"), "primary"]);
    if (c.status === "client_approval") actions.push(["client_approve", t("client_approve"), "green"]);
  }
  if (isSpecialist) {
    if (["new", "documents_received", "ai_review"].includes(c.status)) { actions.push(["__ai", t("run_ai"), "ai"]); actions.push(["take", t("take_case"), "primary"]); }
    if (c.status === "specialist_review") { actions.push(["confirm_release", t("confirm_release"), "green"]); actions.push(["request_info", t("request_info"), "amber"]); actions.push(["submit_supervisor", t("submit_supervisor"), "primary"]); }
  }
  if (isSupervisor && c.status === "supervisor_review") { actions.push(["supervisor_approve", t("supervisor_approve"), "green"]); actions.push(["supervisor_return", t("supervisor_return"), "amber"]); }
  if (isQuality && c.status === "quality_review") { actions.push(["quality_approve", t("quality_approve"), "green"]); actions.push(["quality_return", t("quality_return"), "amber"]); }

  const btnClass = (k) => k === "green" ? "bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]" : k === "amber" ? "bg-[hsl(38_92%_45%)] hover:bg-[hsl(38_92%_40%)]" : k === "ai" ? "bg-[hsl(262_60%_55%)] hover:bg-[hsl(262_60%_50%)]" : "bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]";

  return (
    <div className="space-y-5 gc-fade-up" data-testid="case-detail">
      {/* Header */}
      <div className="rounded-2xl border border-border bg-card p-6">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="font-mono text-xs text-[hsl(158_64%_28%)]">{c.reference}</div>
            <h1 className="font-display text-xl font-bold text-[hsl(217_63%_15%)] mt-1">{lang === "ar" ? c.service_label_ar : c.service_label_en}</h1>
            <div className="text-sm text-muted-foreground mt-0.5">{c.organization_name}</div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <StatusBadge status={c.status} />
            {c.deadline && <div className="text-xs text-[hsl(354_78%_54%)] font-medium">{t("deadline")}: {c.deadline}</div>}
            <a href={`/app/cases/${id}/report`} data-testid="open-report-btn" className="text-xs text-[hsl(158_64%_28%)] font-medium underline">{t("print_report")}</a>
          </div>
        </div>
        <div className="mt-4"><WorkflowPipeline status={c.status} /></div>
        {(c.prepared_by || c.reviewed_by || c.quality_approved_by) && (
          <div className="mt-4 flex flex-wrap gap-4 text-xs text-muted-foreground border-t border-border pt-3">
            <ShieldCheck className="h-4 w-4 text-[hsl(158_64%_28%)]" />
            {c.prepared_by && <span>{t("prepared_by")}: <b className="text-foreground">{c.prepared_by}</b></span>}
            {c.reviewed_by && <span>{t("reviewed_by")}: <b className="text-foreground">{c.reviewed_by}</b></span>}
            {c.quality_approved_by && <span>{t("quality_approved_by")}: <b className="text-foreground">{c.quality_approved_by}</b></span>}
          </div>
        )}
        {actions.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {actions.map(([a, label, kind]) => (
              <Button key={a} data-testid={`action-${a}`} disabled={busy} onClick={() => a === "__ai" ? runAI() : a === "submit_documents" ? doAction("submit_documents") : doAction(a)}
                className={`rounded-full ${btnClass(kind)}`}>
                {kind === "ai" && <Sparkles className="h-4 w-4 me-1.5" />}{label}
              </Button>
            ))}
          </div>
        )}
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2">
          <Tabs defaultValue="overview">
            <TabsList className="flex-wrap h-auto">
              <TabsTrigger value="overview" data-testid="tab-overview">{t("overview")}</TabsTrigger>
              {c.service_type === "advisory"
                ? <TabsTrigger value="advisory" data-testid="tab-advisory">{t("ai_analysis")}</TabsTrigger>
                : <TabsTrigger value="findings" data-testid="tab-findings">{t("findings")} ({c.findings?.length || 0})</TabsTrigger>}
              <TabsTrigger value="requests" data-testid="tab-requests">{t("doc_requests")}</TabsTrigger>
              <TabsTrigger value="documents" data-testid="tab-documents">{t("documents")}</TabsTrigger>
              {isQuality && <TabsTrigger value="qc" data-testid="tab-qc">{t("qc_checklist")}</TabsTrigger>}
              <TabsTrigger value="audit" data-testid="tab-audit">{t("audit_trail")}</TabsTrigger>
              {!isClient && <TabsTrigger value="time" data-testid="tab-time">{t("time_log")}</TabsTrigger>}
            </TabsList>

            {/* Overview */}
            <TabsContent value="overview" className="mt-4 space-y-4">
              {c.description && <div className="rounded-xl border border-border bg-card p-5"><div className="text-xs text-muted-foreground mb-1">{t("description")}</div><p className="text-sm">{c.description}</p></div>}
              {c.question && <div className="rounded-xl border border-border bg-card p-5"><div className="text-xs text-muted-foreground mb-1">{t("question_field")}</div><p className="text-sm">{c.question}</p></div>}
              {isClient && !aiReleased && (c.ai_analysis || c.advisory_note) && <AiWaitingBanner />}
              {relDecisions.length > 0 && (
                <div className="rounded-xl border border-border bg-secondary/30 p-5" data-testid="related-decisions">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(217_63%_15%)] mb-2"><BookMarked className="h-4 w-4" /> {t("related_decisions")}</div>
                  <div className="space-y-1.5">
                    {relDecisions.map((d) => (
                      <a key={d.reference} href={d.url} target="_blank" rel="noreferrer" data-testid={`rel-decision-${d.reference}`} className="block text-sm rounded-lg p-2 hover:bg-card border border-transparent hover:border-border transition-colors">
                        <span className="font-mono text-xs text-[hsl(158_64%_28%)]">{d.reference}</span> — {lang === "ar" ? d.title_ar : d.title_en}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              {!isClient && c.drafts?.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-5" data-testid="case-drafts">
                  <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(217_63%_15%)] mb-3"><ScrollText className="h-4 w-4" /> {t("drafts_title")}</div>
                  <div className="space-y-2">
                    {c.drafts.map((d) => (
                      <div key={d.id} data-testid={`case-draft-${d.id}`} className="rounded-lg border border-border p-3">
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] px-2 py-0.5 rounded-full bg-secondary text-muted-foreground">{d.kind === "communication" ? t("draft_kind_communication") : t("draft_kind_research")}</span>
                            <span className="text-sm font-medium text-foreground">{d.title}</span>
                          </div>
                          <button data-testid={`draft-del-${d.id}`} onClick={() => delDraft(d.id)} className="text-xs text-[hsl(354_60%_45%)] hover:underline">{t("draft_delete")}</button>
                        </div>
                        <p className="text-sm text-muted-foreground whitespace-pre-wrap mt-2 leading-relaxed">{d.content}</p>
                        <div className="text-[10px] text-muted-foreground mt-2">{d.created_by_name}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {c.ai_analysis && (!isClient || aiReleased) && (
                <div className="rounded-xl border border-[hsl(262_60%_88%)] bg-[hsl(262_60%_98%)] p-5">
                  <div className="flex items-center gap-2 text-[hsl(262_60%_45%)] text-sm font-semibold mb-2"><Sparkles className="h-4 w-4" /> {t("ai_analysis")}</div>
                  <div className="text-xs font-semibold text-[hsl(158_64%_28%)] mb-1">{t("client_summary")}</div>
                  <p className="text-sm leading-relaxed">{lang === "ar" ? (c.ai_analysis.client_summary_ar || c.ai_analysis.summary_ar) : (c.ai_analysis.client_summary_en || c.ai_analysis.summary_en)}</p>
                  {!isClient && (c.ai_analysis.specialist_detail_en || c.ai_analysis.specialist_detail_ar) && (
                    <div className="mt-3 pt-3 border-t border-[hsl(262_60%_88%)]">
                      <div className="text-xs font-semibold text-[hsl(262_60%_45%)] mb-1">{t("specialist_detail")}</div>
                      <p className="text-sm leading-relaxed">{lang === "ar" ? c.ai_analysis.specialist_detail_ar : c.ai_analysis.specialist_detail_en}</p>
                    </div>
                  )}
                  <div className="grid grid-cols-3 gap-3 mt-4">
                    <div><div className="text-[10px] text-muted-foreground">{t("overall_risk")}</div><RiskPill level={c.ai_analysis.overall_risk} /></div>
                    <div><div className="text-[10px] text-muted-foreground">{t("transactions_reviewed")}</div><div className="font-mono font-bold">{c.ai_analysis.transactions_reviewed}</div></div>
                    <div><div className="text-[10px] text-muted-foreground">{t("exceptions")}</div><div className="font-mono font-bold">{c.ai_analysis.exceptions_identified}</div></div>
                  </div>
                  <RegRefs refs={c.ai_analysis.regulatory_references} />
                </div>
              )}
              <FeedbackCard c={c} isClient={isClient} onDone={load} />
              <div className="rounded-xl bg-secondary/50 border border-border p-4 text-xs text-muted-foreground">{t("disclaimer_note")}</div>
            </TabsContent>

            {/* Advisory */}
            <TabsContent value="advisory" className="mt-4 space-y-3">
              {isClient && !aiReleased ? <AiWaitingBanner /> : !c.advisory_note ? <div className="text-sm text-muted-foreground rounded-xl border border-border p-5">{t("run_ai")}</div> : (
                <div className="rounded-xl border border-border bg-card p-5 space-y-3 text-sm">
                  <Field label={t("client_summary")} value={lang === "ar" ? (c.advisory_note.client_summary_ar || c.advisory_note.facts_understood_ar) : (c.advisory_note.client_summary_en || c.advisory_note.facts_understood_en)} />
                  <Field label={t("recommendation")} value={lang === "ar" ? c.advisory_note.potential_treatment_ar : c.advisory_note.potential_treatment_en} />
                  {!isClient && <Field label={t("specialist_detail")} value={lang === "ar" ? c.advisory_note.specialist_detail_ar : c.advisory_note.specialist_detail_en} />}
                  <Field label={t("risk_level")} value={lang === "ar" ? c.advisory_note.risk_considerations_ar : c.advisory_note.risk_considerations_en} />
                  <RegRefs refs={c.advisory_note.regulatory_references} />
                  <div><ConfidenceBadge level={c.advisory_note.confidence} /></div>
                </div>
              )}
            </TabsContent>

            {/* Findings */}
            <TabsContent value="findings" className="mt-4 space-y-3">
              {(c.findings || []).length === 0 && <div className="text-sm text-muted-foreground rounded-xl border border-border p-5">{isSpecialist ? t("run_ai") : "—"}</div>}
              {(c.findings || []).map((f) => (
                <div key={f.id} data-testid={`finding-${f.id}`} className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium bg-secondary rounded-full px-2.5 py-1">{f.category}</span>
                      <RiskPill level={f.risk_level} />
                      {f.origin === "ai" && <span className="text-[10px] text-[hsl(262_60%_55%)] flex items-center gap-1"><Sparkles className="h-3 w-3" />AI</span>}
                    </div>
                    <ConfidenceBadge level={f.confidence} />
                  </div>
                  <p className="text-sm mt-3">{lang === "ar" ? f.description_ar : f.description_en}</p>
                  {(f.recommendation_en || f.recommendation_ar) && <p className="text-xs text-muted-foreground mt-2"><b>{t("recommendation")}:</b> {lang === "ar" ? f.recommendation_ar : f.recommendation_en}</p>}
                  {f.source && <p className="text-xs text-[hsl(158_64%_28%)] mt-1">{f.source}</p>}
                  <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border">
                    {(isSupervisor) && <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={f.supervisor_approved} onCheckedChange={(v) => updateFinding(f.id, { supervisor_approved: !!v })} data-testid={`sup-approve-${f.id}`} /> {t("supervisor_approved")}</label>}
                    {(isQuality) && <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={f.quality_approved} onCheckedChange={(v) => updateFinding(f.id, { quality_approved: !!v })} data-testid={`qc-approve-${f.id}`} /> {t("quality_approved_by")}</label>}
                    {f.supervisor_approved && <span className="text-[10px] text-[hsl(158_64%_28%)]">✓ {t("supervisor_approved")}</span>}
                  </div>
                </div>
              ))}
              {isSpecialist && (
                <div className="rounded-xl border border-dashed border-border p-5 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium"><Plus className="h-4 w-4" /> {t("add_finding")}</div>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Select value={newFinding.category} onValueChange={(v) => setNewFinding({ ...newFinding, category: v })}>
                      <SelectTrigger data-testid="finding-category"><SelectValue /></SelectTrigger>
                      <SelectContent className="max-h-60">{CATEGORIES.map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={newFinding.risk_level} onValueChange={(v) => setNewFinding({ ...newFinding, risk_level: v })}>
                      <SelectTrigger data-testid="finding-risk"><SelectValue /></SelectTrigger>
                      <SelectContent>{["low", "medium", "high", "critical"].map((x) => <SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <Textarea data-testid="finding-desc" placeholder={t("description")} value={newFinding.description} onChange={(e) => setNewFinding({ ...newFinding, description: e.target.value })} />
                  <Input data-testid="finding-rec" placeholder={t("recommendation")} value={newFinding.recommendation} onChange={(e) => setNewFinding({ ...newFinding, recommendation: e.target.value })} />
                  <Button data-testid="finding-add-btn" onClick={addFinding} className="rounded-full bg-[hsl(217_63%_15%)]">{t("add_finding")}</Button>
                </div>
              )}
            </TabsContent>

            {/* Doc Requests */}
            <TabsContent value="requests" className="mt-4 space-y-3">
              {(c.document_requests || []).length === 0 && <div className="text-sm text-muted-foreground rounded-xl border border-border p-5">—</div>}
              {(c.document_requests || []).map((r) => (
                <div key={r.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-4">
                  <div>
                    <div className="text-sm font-medium">{lang === "ar" ? (r.name_ar || r.name) : (r.name_en || r.name)}</div>
                    {r.description && <div className="text-xs text-muted-foreground">{r.description}</div>}
                  </div>
                  <span className={`text-xs font-medium rounded-full px-2.5 py-1 ${r.status === "received" ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>{r.status === "received" ? t("received") : t("pending")}</span>
                </div>
              ))}
              {(isSpecialist || isSupervisor) && (
                <div className="rounded-xl border border-dashed border-border p-5 space-y-3">
                  <div className="flex items-center gap-2 text-sm font-medium"><Plus className="h-4 w-4" /> {t("add_doc_request")}</div>
                  <Input data-testid="req-name" placeholder={t("documents")} value={newReq.name} onChange={(e) => setNewReq({ ...newReq, name: e.target.value })} />
                  <Input data-testid="req-desc-field" placeholder={t("description")} value={newReq.description} onChange={(e) => setNewReq({ ...newReq, description: e.target.value })} />
                  <Button data-testid="req-add-btn" onClick={addReq} className="rounded-full bg-[hsl(217_63%_15%)]">{t("add_doc_request")}</Button>
                </div>
              )}
            </TabsContent>

            {/* Documents */}
            <TabsContent value="documents" className="mt-4 space-y-3">
              <input ref={fileRef} type="file" hidden onChange={upload} data-testid="case-file-input" />
              <Button data-testid="case-upload-btn" onClick={() => fileRef.current?.click()} className="rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]"><Upload className="h-4 w-4 me-1.5" /> {t("upload_document")}</Button>
              <CaseDocuments caseId={id} />
            </TabsContent>

            {/* QC */}
            {isQuality && (
              <TabsContent value="qc" className="mt-4">
                <div className="rounded-xl border border-border bg-card p-5 space-y-2">
                  <div className="text-sm font-semibold text-[hsl(217_63%_15%)] mb-3">{t("qc_checklist")}</div>
                  {Object.keys(QC_LABELS).map((k) => (
                    <label key={k} className="flex items-center gap-3 py-1.5 cursor-pointer">
                      <Checkbox data-testid={`qc-${k}`} checked={c.qc_checklist?.[k]} onCheckedChange={(v) => toggleQC(k, !!v)} />
                      <span className="text-sm">{QC_LABELS[k][lang]}</span>
                    </label>
                  ))}
                </div>
              </TabsContent>
            )}

            {/* Audit */}
            <TabsContent value="audit" className="mt-4">
              <div className="rounded-xl border border-border bg-card p-5">
                <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(217_63%_15%)] mb-4"><ScrollText className="h-4 w-4" /> {t("audit_trail")}</div>
                <div className="space-y-3">
                  {(c.audit_log || []).map((a) => (
                    <div key={a.id} className="flex gap-3 text-sm border-s-2 border-border ps-3">
                      <div className="flex-1">
                        <div className="font-medium">{a.action} <span className="text-muted-foreground font-normal">— {a.user_name}</span></div>
                        {(a.from_status || a.to_status) && <div className="text-xs text-muted-foreground">{a.from_status || "∅"} → {a.to_status}</div>}
                        {a.comment && <div className="text-xs mt-0.5">{a.comment}</div>}
                        <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{a.at?.slice(0, 16).replace("T", " ")}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
            {!isClient && (
              <TabsContent value="time" className="mt-4">
                <div className="rounded-xl border border-border bg-card p-5">
                  <div className="flex items-center justify-between mb-3">
                    <div className="text-sm font-semibold text-[hsl(217_63%_15%)]">{t("time_log")}</div>
                    <div className="text-xs text-muted-foreground">{t("total_time")}: <b className="text-foreground">{Math.round(((c.time_entries || []).reduce((s, e) => s + (e.minutes || 0), 0)) / 6) / 10} {t("hours")}</b></div>
                  </div>
                  <div className="space-y-2 mb-4">
                    {(c.time_entries || []).length === 0 && <div className="text-xs text-muted-foreground">—</div>}
                    {(c.time_entries || []).map((e) => (
                      <div key={e.id} data-testid={`time-entry-${e.id}`} className="flex items-center justify-between text-sm border-t border-border pt-2">
                        <div><span className="font-medium">{e.user_name}</span> <span className="text-xs text-muted-foreground">— {e.note}</span></div>
                        <div className="font-mono text-xs">{e.minutes} {t("minutes")}</div>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    {!timer.running ? (
                      <Button data-testid="timer-start" onClick={startTimer} className="rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]">▶ {t("start_timer")}</Button>
                    ) : (
                      <Button data-testid="timer-stop" onClick={stopTimer} className="rounded-full bg-[hsl(354_78%_54%)] hover:bg-[hsl(354_78%_48%)]">■ {t("stop_timer")}</Button>
                    )}
                    {timer.running && <span data-testid="timer-elapsed" className="font-mono text-sm font-semibold text-[hsl(217_63%_15%)]">{Math.floor(timer.elapsed / 60)}:{String(timer.elapsed % 60).padStart(2, "0")}</span>}
                  </div>
                  <div className="flex gap-2">
                    <Input data-testid="time-minutes" type="number" placeholder={t("minutes")} value={timeForm.minutes} onChange={(e) => setTimeForm({ ...timeForm, minutes: e.target.value })} className="max-w-[120px]" />
                    <Input data-testid="time-note" placeholder={t("time_note")} value={timeForm.note} onChange={(e) => setTimeForm({ ...timeForm, note: e.target.value })} />
                    <Button data-testid="time-add" onClick={logTime} className="rounded-full bg-[hsl(217_63%_15%)]">{t("add_time")}</Button>
                  </div>
                </div>
              </TabsContent>
            )}
          </Tabs>
        </div>

        {/* Right panel: sources + comments */}
        <div className="space-y-5">
          {!isClient && (
            <div className="rounded-2xl border border-border bg-card p-5">
              <div className="flex items-center gap-2 text-sm font-semibold text-[hsl(217_63%_15%)] mb-3"><BookMarked className="h-4 w-4" /> {t("sources")}</div>
              <div className="space-y-2">
                {SOURCES.map((s, i) => (
                  <div key={i} className="rounded-lg border border-border px-3 py-2">
                    <div className="text-xs font-medium">{s.t[lang]}</div>
                    <div className="text-[10px] text-muted-foreground">{s.s[lang]}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          <div className="rounded-2xl border border-border bg-card p-5">
            <div className="text-sm font-semibold text-[hsl(217_63%_15%)] mb-3">{t("comments")}</div>
            <div className="space-y-3 max-h-64 overflow-y-auto gc-scroll">
              {(c.comments || []).length === 0 && <div className="text-xs text-muted-foreground">—</div>}
              {(c.comments || []).map((m) => (
                <div key={m.id} className="text-sm">
                  <div className="font-medium text-xs">{m.user_name}</div>
                  <div className="text-sm">{m.text}</div>
                  <div className="text-[10px] text-muted-foreground font-mono">{m.at?.slice(0, 16).replace("T", " ")}</div>
                </div>
              ))}
            </div>
            <div className="mt-3 flex gap-2">
              <Input data-testid="comment-input" value={comment} onChange={(e) => setComment(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendComment()} placeholder={t("add_comment")} />
              <Button data-testid="comment-send" onClick={sendComment} className="bg-[hsl(217_63%_15%)]">{t("send")}</Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function AiWaitingBanner() {
  const { t } = useI18n();
  return (
    <div data-testid="ai-waiting-banner" className="rounded-xl border border-[hsl(38_92%_80%)] bg-[hsl(38_92%_97%)] p-5">
      <div className="flex items-center gap-2 text-[hsl(30_80%_38%)] text-sm font-semibold mb-1">
        <Sparkles className="h-4 w-4" /> {t("ai_waiting_title")}
      </div>
      <p className="text-sm text-muted-foreground leading-relaxed">{t("ai_waiting_body")}</p>
    </div>
  );
}

function Field({ label, value }) {
  if (!value) return null;
  return <div><div className="text-xs text-muted-foreground mb-1">{label}</div><p className="leading-relaxed">{value}</p></div>;
}

function RegRefs({ refs }) {
  const { t, lang } = useI18n();
  if (!refs || refs.length === 0) return null;
  return (
    <div className="mt-4 pt-3 border-t border-border/60">
      <div className="text-xs font-semibold text-[hsl(217_63%_15%)] mb-2 flex items-center gap-1.5"><BookMarked className="h-3.5 w-3.5" /> {t("regulatory_refs")}</div>
      <div className="flex flex-wrap gap-2" data-testid="regulatory-references">
        {refs.map((r, i) => (
          <a key={i} href={r.url} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-lg border border-[hsl(158_64%_28%)]/30 bg-[hsl(158_64%_98%)] px-2.5 py-1.5 text-xs hover:border-[hsl(158_64%_28%)] transition-colors">
            <span className="font-mono font-semibold text-[hsl(158_64%_28%)]">{r.article}</span>
            <span className="text-foreground/70">{lang === "ar" ? r.title_ar : r.title_en}</span>
          </a>
        ))}
      </div>
    </div>
  );
}

function FeedbackCard({ c, isClient, onDone }) {
  const { t } = useI18n();
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const completed = ["completed", "client_approval", "closed"].includes(c.status);
  if (!isClient || !completed) return null;
  if (c.feedback) {
    return (
      <div className="rounded-xl border border-[hsl(158_64%_28%)]/30 bg-[hsl(158_64%_98%)] p-5">
        <div className="text-sm font-semibold text-[hsl(217_63%_15%)] mb-1">{t("feedback")}</div>
        <div className="text-lg">{"★".repeat(c.feedback.rating)}{"☆".repeat(5 - c.feedback.rating)}</div>
        {c.feedback.comment && <p className="text-sm text-muted-foreground mt-1">{c.feedback.comment}</p>}
      </div>
    );
  }
  const send = async () => {
    try { await api.post(`/cases/${c.id}/feedback`, { rating, comment }); toast.success(t("thank_feedback")); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="rounded-xl border border-border bg-card p-5" data-testid="feedback-card">
      <div className="text-sm font-semibold text-[hsl(217_63%_15%)] mb-2">{t("rate_service")}</div>
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map((n) => (
          <button key={n} data-testid={`star-${n}`} onClick={() => setRating(n)} className="text-2xl" style={{ color: n <= rating ? "hsl(41 72% 48%)" : "hsl(214 20% 80%)" }}>★</button>
        ))}
      </div>
      <Input data-testid="feedback-comment" value={comment} onChange={(e) => setComment(e.target.value)} placeholder={t("add_comment")} className="mb-3" />
      <Button data-testid="feedback-submit" onClick={send} className="rounded-full bg-[hsl(158_64%_28%)] hover:bg-[hsl(158_64%_24%)]">{t("submit_feedback")}</Button>
    </div>
  );
}

function CaseDocuments({ caseId }) {
  const { t, lang } = useI18n();
  const [docs, setDocs] = useState([]);
  useEffect(() => { api.get(`/documents?case_id=${caseId}`).then((r) => setDocs(r.data)).catch(() => {}); }, [caseId]);
  if (docs.length === 0) return <div className="text-sm text-muted-foreground">{t("no_documents")}</div>;
  return (
    <div className="space-y-2">
      {docs.map((d) => (
        <div key={d.id} className="flex items-center justify-between rounded-xl border border-border bg-card p-3">
          <div className="flex items-center gap-3"><FileText className="h-4 w-4 text-[hsl(217_63%_15%)]" /><div><div className="text-sm font-medium">{d.name}</div><div className="text-[10px] text-muted-foreground">{d.folder} · {(d.size / 1024).toFixed(0)} KB</div></div></div>
          <a href={`${API}/documents/${d.id}/download`} target="_blank" rel="noreferrer" className="text-xs text-[hsl(158_64%_28%)]">{t("download")}</a>
        </div>
      ))}
    </div>
  );
}
